/**
 * SkiSupply — cursor-reactive card motion.
 *
 * The card tilts toward the pointer, lifts, and carries a specular highlight
 * that tracks the cursor across its surface. The technique is borrowed from
 * the interaction work on Linear, Stripe and the Apple TV+ poster grid.
 *
 * Three things make this feel good rather than gimmicky:
 *
 *   1. Spring physics, not easing curves. Every value is integrated with
 *      velocity and damping, so motion overshoots slightly and settles the way
 *      a physical object does. A cubic-bezier always arrives on an exact
 *      schedule, which is precisely what makes it read as animation.
 *
 *   2. Lag. The card does not track the cursor exactly; it chases it. That
 *      small, constant deficit is what gives the card apparent mass. Perfect
 *      tracking feels weightless and cheap.
 *
 *   3. Restraint. Six degrees of tilt, ten pixels of lift. Enough that the eye
 *      registers depth, little enough that text stays readable and the product
 *      photograph stays the subject.
 *
 * Everything animates on transform and opacity only — no property here can
 * trigger layout or paint, so the whole effect runs on the compositor. The
 * loop stops itself once every spring has settled.
 *
 * Progressive enhancement: this file adds `ss-motion` to <html>. The stylesheet
 * only applies the 3D treatment under that class, so if this script fails to
 * load, cards fall back to a plain CSS lift. Nothing here is required for a
 * card to be clickable, and no handler calls preventDefault.
 */

(() => {
  'use strict';

  // Pointer-driven tilt is meaningless on touch, and unwelcome for anyone who
  // has asked their system for less motion.
  if (!matchMedia('(any-pointer: fine)').matches) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* Three families of hoverable thing, one engine.

     A button is a tenth the area of a product card, so the card's six degrees
     of tilt on a button reads as a glitch rather than as depth — the eye has
     no large surface to register the rotation against, it just sees the text
     skew. Each family therefore keeps the same physics and the same feel, and
     only the amplitudes change: less rotation and less travel as the target
     gets smaller. That is what makes a button feel like it belongs to the same
     object as the card without looking like a shrunken copy of one. */
  const PROFILES = [
    {
      selector: '.product-card, .collection-card',
      maxTilt: 6,          // degrees at the edge
      lift: -10,           // px toward the viewer

      /* The whole card comes forward, border included, rather than the frame
         holding still while the photo swells inside it. Raised from 1.035
         because at that value the card's own growth was smaller than the
         parallax scale on the image, so the eye read the picture as moving and
         the card as stationary — a border painted on, not an object lifting. */
      scale: 1.06,
    },
    {
      selector: '.promo-card',
      maxTilt: 4,
      lift: -8,
      scale: 1.02,         // these are large photos; scale is already dramatic
    },
    {
      /* `.promo-card__link` is deliberately absent: it sits inside a promo
         card, so it would match first and take the pointer, leaving the card
         it lives on frozen while the little link tilted on its own. */
      selector:
        '.button, .button-custom, .button-secondary, .deal-bar__button, .slideshow-control',
      maxTilt: 9,          // small surface, so more degrees to read as the same tilt
      lift: -3,
      scale: 1.045,
    },
  ];

  const SELECTOR = PROFILES.map((p) => p.selector).join(', ');

  /** Which profile governs this element. Nearest match wins. */
  function profileFor(el) {
    for (const p of PROFILES) if (el.matches(p.selector)) return p;
    return PROFILES[0];
  }

  /* Spring constants. Stiffness sets how hard it pulls toward the target;
     damping sets how much velocity survives each frame. Lower damping = more
     overshoot. Tilt is damped harder than lift: a wobbling card reads as
     broken, a bouncing one reads as responsive. */
  const TILT = { stiffness: 0.16, damping: 0.72 };
  const BODY = { stiffness: 0.13, damping: 0.68 };
  const FADE = { stiffness: 0.2, damping: 0.72 };

  const EPSILON = 0.0004;

  const makeSpring = (cfg, initial = 0) => ({
    value: initial,
    target: initial,
    velocity: 0,
    stiffness: cfg.stiffness,
    damping: cfg.damping,
  });

  /** Advance one spring by a frame. Returns false once it has come to rest. */
  function advance(spring) {
    spring.velocity += (spring.target - spring.value) * spring.stiffness;
    spring.velocity *= spring.damping;
    spring.value += spring.velocity;

    const moving =
      Math.abs(spring.velocity) > EPSILON ||
      Math.abs(spring.target - spring.value) > EPSILON;

    if (!moving) {
      spring.value = spring.target;
      spring.velocity = 0;
    }
    return moving;
  }

  const active = new Map();
  let frame = null;

  function stateFor(card) {
    let state = active.get(card);
    if (!state) {
      state = {
        profile: profileFor(card),
        rotateX: makeSpring(TILT),
        rotateY: makeSpring(TILT),
        lift: makeSpring(BODY),
        scale: makeSpring(BODY, 1),
        glow: makeSpring(FADE),
        pointerX: 50,
        pointerY: 50,
      };
      active.set(card, state);
      card.style.willChange = 'transform';
    }
    return state;
  }

  function render() {
    let stillMoving = false;

    active.forEach((state, card) => {
      const moving = [
        advance(state.rotateX),
        advance(state.rotateY),
        advance(state.lift),
        advance(state.scale),
        advance(state.glow),
      ].some(Boolean);

      const style = card.style;
      style.setProperty('--ss-rx', state.rotateX.value.toFixed(3));
      style.setProperty('--ss-ry', state.rotateY.value.toFixed(3));
      style.setProperty('--ss-lift', state.lift.value.toFixed(3));
      style.setProperty('--ss-scale', state.scale.value.toFixed(4));
      style.setProperty('--ss-glow', state.glow.value.toFixed(4));
      style.setProperty('--ss-mx', state.pointerX.toFixed(2));
      style.setProperty('--ss-my', state.pointerY.toFixed(2));

      if (moving) {
        stillMoving = true;
      } else if (state.glow.target === 0) {
        // Fully returned to rest: drop the compositor hint and stop tracking it.
        style.willChange = '';
        active.delete(card);
      }
    });

    frame = stillMoving ? requestAnimationFrame(render) : null;
  }

  const start = () => {
    if (frame === null) frame = requestAnimationFrame(render);
  };

  /* Delegated from the document so cards added later — pagination, filtering,
     section re-renders, quick add — are picked up without re-binding. */
  document.addEventListener(
    'pointermove',
    (event) => {
      const card = event.target.closest?.(SELECTOR);
      if (!card) {
        // The pointer is over the page but not over anything that tilts, so
        // nothing should still be holding a tilt.
        pointerX = event.clientX;
        pointerY = event.clientY;
        releaseAll();
        return;
      }

      /* A button inside a card matches first. Let it take the pointer and
         leave the card at rest, or the two tilts compound and the button
         swims across a surface that is itself moving. */
      pointerX = event.clientX;
      pointerY = event.clientY;

      const box = card.getBoundingClientRect();
      if (!box.width || !box.height) return;

      const x = (event.clientX - box.left) / box.width;
      const y = (event.clientY - box.top) / box.height;

      const state = stateFor(card);
      const p = state.profile;
      state.rotateY.target = (x - 0.5) * 2 * p.maxTilt;
      state.rotateX.target = -(y - 0.5) * 2 * p.maxTilt;
      state.lift.target = p.lift;
      state.scale.target = p.scale;
      state.glow.target = 1;
      state.pointerX = x * 100;
      state.pointerY = y * 100;

      releaseAll(card);
      start();
    },
    { passive: true }
  );

  /** Send one card home. */
  function release(card) {
    const state = active.get(card);
    if (!state) return;
    state.rotateX.target = 0;
    state.rotateY.target = 0;
    state.lift.target = 0;
    state.scale.target = 1;
    state.glow.target = 0;
  }

  /** Send every card home except the one currently under the pointer. */
  function releaseAll(except) {
    let any = false;
    active.forEach((_state, card) => {
      if (card === except) return;
      release(card);
      any = true;
    });
    if (any) start();
  }

  document.addEventListener(
    'pointerout',
    (event) => {
      const card = event.target.closest?.(SELECTOR);
      if (!card) return;
      // Moving between children of the same card is not a departure.
      if (event.relatedTarget && card.contains(event.relatedTarget)) return;

      release(card);
      start();
    },
    { passive: true }
  );

  /* Scrolling is the case `pointerout` cannot cover: the page moves out from
     under a stationary cursor, so the card leaves the pointer rather than the
     pointer leaving the card, and no pointer event is generated at all.

     Releasing every card on scroll is the obvious fix and the wrong one — this
     page emits about 23 scroll events a second on its own (the smooth-scroll
     loop settling, card galleries advancing), so that cancels a live hover
     several times a frame and no card ever tilts. Instead, ask what is actually
     under the cursor now. Anything else lets go; a card still under it keeps
     its tilt, which is what should happen when you scroll with the mouse
     resting on a card.

     Throttled to one hit test per frame — elementFromPoint forces layout. */
  let pointerX = null;
  let pointerY = null;
  let hitTestQueued = false;

  function releaseWhatThePointerLeft() {
    hitTestQueued = false;
    if (pointerX === null || active.size === 0) return;
    const under = document.elementFromPoint(pointerX, pointerY);
    releaseAll(under ? under.closest(SELECTOR) : null);
  }

  document.addEventListener(
    'scroll',
    () => {
      if (hitTestQueued || active.size === 0) return;
      hitTestQueued = true;
      requestAnimationFrame(releaseWhatThePointerLeft);
    },
    { capture: true, passive: true }
  );

  // The cursor left the window, or focus went elsewhere mid-hover.
  document.addEventListener('pointerleave', () => releaseAll(), { passive: true });
  window.addEventListener('blur', () => releaseAll(), { passive: true });

  // Springs integrate per frame, so a backgrounded tab would otherwise resume
  // mid-flight. Settle everything immediately instead.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) return;
    active.forEach((state, card) => {
      card.style.willChange = '';
      card.style.removeProperty('--ss-rx');
      card.style.removeProperty('--ss-ry');
      card.style.removeProperty('--ss-lift');
      card.style.removeProperty('--ss-scale');
      card.style.removeProperty('--ss-glow');
      active.delete(card);
    });
    if (frame !== null) {
      cancelAnimationFrame(frame);
      frame = null;
    }
  });

  document.documentElement.classList.add('ss-motion');
})();
